import {set as setStat} from './stats';
import {
	createInteractionId,
	mark,
	types as metrics,
	setInteractionId
} from '@servicenow/ui-metrics';
import {buildPropertiesValidator} from './utils/schema';
import {
	sandbox,
	locations,
	guid,
	pendingNetworkActionCountForHost,
	clearPendingNetworkActionsForHost,
	addPendingNetworkActionForHostCb,
	isEqual
} from '@servicenow/ui-internal';
import {normalizeConfig} from './utils/config';
import {
	COMPONENT_ID_ATTRIBUTE,
	DEFER_ATTRIBUTE,
	COMPONENT_CONNECTED,
	COMPONENT_DISCONNECTED,
	COMPONENT_BOOTSTRAPPED,
	COMPONENT_RENDERED as COMPONENT_RENDERED_ACTION_TYPE,
	NOW_MODE_PROP,
	NOW_ENABLE_PROPERTY_SETTING_PREVENTION_PROP,
	COMPONENT_MODE_SUSPEND,
	COMPONENT_MODE_DETACH,
	NOW_ID_ATTR,
	COMPONENT_DOM_READY,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	HELPERS_SCHEDULED as HELPERS_SCHEDULED_CONST,
	HELPERS_DEFER as HELPERS_DEFER_CONST,
	HELPERS_IMMEDIATE as HELPERS_IMMEDIATE_CONST,
	STATE_UPDATED,
	COMPONENT_INTERNAL_ACTION,
	COMPONENT_PROPERTY_UPDATE_REQUESTED,
	COMPONENT_RENDER_REQUESTED,
	COMPONENT_DOM_TREE_READY,
	COMPONENT_TREE_RENDERED,
	SHADOW_ROOT_MODE_CLOSED,
	NOW_PLACEHOLDER_HEIGHT_PROP,
	NOW_PLACEHOLDER_WIDTH_PROP,
	NOW_HOIST_PROP,
	HOIST_ATTR,
	NOW_PRIORITY_PROP,
	NOW_PRIORITY_ATTR,
	PRIORITY_LEVEL_LOW
} from './constants';
import {
	PRESENTATIONAL,
	VARIABLES,
	COMPONENT_ID,
	CONNECTED,
	PROPERTY_DEFS,
	STATE,
	PROPS,
	DISPATCH,
	CONDITIONAL_DISPATCH,
	BOOTSTRAPPED_HANDLED,
	CONNECTED_HANDLED,
	UNHOIST,
	HOIST_CHANGED,
	HOIST_PARENT_CHANGED,
	GET_RESOLVED_PROPERTY,
	ROOT_NODE,
	VERSION,
	ATTACHED_ROOT_NODE,
	DEFERRED,
	CONFIG,
	ACTION_HANDLERS,
	TARGETED_ACTION_TYPES,
	RENDERED_ONCE,
	TREE_RENDERED_ONCE,
	HELPERS_SCHEDULED,
	HELPERS_DEFER,
	HELPERS_IMMEDIATE,
	RENDER,
	STYLE_PROXY,
	SET_PROPERTY,
	PREVIOUS_RENDER_STATE,
	PREVIOUS_RENDER_PROPERTIES,
	PREVIOUS_TREE_RENDER_STATE,
	PREVIOUS_TREE_RENDER_PROPERTIES,
	CHECK_IF_TREE_RENDERED,
	CHILD_IS_RENDERING,
	CHILDREN_TO_BE_RENDERED,
	CHILD_HAS_RENDERED,
	NOTIFY_PARENT_THAT_CHILD_IS_RENDERING,
	NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED,
	NEEDS_TO_KNOW_ABOUT_TREE_RENDERS,
	ANCESTOR_ROOT_NODE,
	UPDATE_AFFECTED_COMPONENTS,
	RUNTIME_ACTION_HANDLERS,
	INTERSECTED,
	USING_INTERSECTION_OBSERVER,
	INTERSECTION_TRIGGERED,
	REMOVE_INTERSECTED_STYLES,
	DETACHED_SHADOW_ROOT_CHILDREN_FRAGMENT,
	DETACHED_COMPONENT_PROPERTY_MAP,
	LOW_PRIORITY_PENDING,
	CONNECTED_TIMESTAMP,
	CHILDREN_TO_BE_INTERACTIVE,
	SELECTABLE_PROPERTIES,
	TREE_RENDERED_ONCE_TIMESTAMP,
	RENDERED_ONCE_TIMESTAMP,
	CONNECTED_DEFERRED_TIMESTAMP,
	CONTEXT,
	PROPERTIES_WITH_SELECTOR
} from './symbols';
import {
	assertValidPropertyNames,
	resolveProperties,
	getInitialPropertyValue,
	isReflecting,
	clearReflection,
	filterPropertyDefinitions,
	shouldSetAttribute,
	shouldSetProperty,
	toPropertyName,
	toAttributeName,
	createLifecycleFunctions,
	measure,
	selectablePropertyResolver,
	triggerComponentUpdates,
	updateSelectedPropertyMappings,
	camelCaseProperties,
	unescapeReservedCharacters,
	isHoisted,
	getHoistTarget,
	emitNowUiEvent,
	getHostVariables,
	getRootNode,
	applyMetrics,
	resolveComputedProperties,
	computeProperties,
	intersectionObserver,
	applyIntersectStyles,
	shouldOptOutOfDeepCompareForPage,
	markComponentInteractive,
	createOnStateChangeArgs
} from './utils';

import createContextProxy from './context/createContextProxy';
import {removeConsumer as removeContextConsumer} from './context/consumers';
import validateContext from './context/validateContext';
import {property as parseProperty, properties as parseProperties} from './utils/parse';
import log from './log';
import {
	createDispatcher,
	createConditionalDispatch,
	addActionHandlers,
	removeActionHandlers
} from './actions';
import createHelpers from './utils/helpers';
import {version} from '../package.json';
import {shouldHoistAttribute, hoistAttribute, unhoistAttribute, default as hoist} from './hoist';
import applyStyles from './applyStyles';
import removeStyles from './removeStyles';
import {
	schedule,
	types,
	countHighPriorityTasks,
	countMediumPriorityTasks,
	addQueueEmptyForElCb
} from './scheduler';
import {default as shapeRenderData, reflectProperty} from './shape-render-data';
import {
	getAffected,
	resolveSelectedProperty,
	resolveSelectedProperties,
	removeMappings,
	getComponentAffected
} from './property-selectors';
import {mergeBehaviorProperties} from './utils/config/mergeBehaviorProperties';
import {normalizeBehaviors} from './utils/config/normalizeBehaviors';
import {getConfigItem, configConstants} from '@servicenow/ui-config';
import quickEquals from './utils/helpers/quickEquals';
import updateResolvedSelectedPropertyMappings from './utils/updateResolvedSelectedPropertyMappings';
import addResolvedSelectedPropertyMappingWithResolvedValue from './utils/addResolvedSelectedPropertyMappingWithResolvedValue';
import updatePropertiesWithSelector from './property-selectors/updatePropertiesWithSelector';

const {ENABLE_SCHEDULER_QUEUE_COUNTS, METRICS_COMPONENT_INTERACTIVE_ENABLED} = configConstants;
const enableSchedulerQueueCounts = getConfigItem(ENABLE_SCHEDULER_QUEUE_COUNTS);
const {COMPONENT_RENDER, DEFER_IDLE} = types;
const {ENABLE_PROPERTY_DEEP_COMPARE} = configConstants;

const enableDeepPropertyCompare = getConfigItem(ENABLE_PROPERTY_DEEP_COMPARE);
let optOutDeepCompare = shouldOptOutOfDeepCompareForPage('property');
window.addEventListener(
	'popstate',
	() => (optOutDeepCompare = shouldOptOutOfDeepCompareForPage('property'))
);

const shouldMarkInteractive = getConfigItem(METRICS_COMPONENT_INTERACTIVE_ENABLED);
const idleCallback = shouldMarkInteractive
	? el => markComponentInteractive(el, componentRegistryForInteractive)
	: () => null;
let componentRegistryForInteractive;
addPendingNetworkActionForHostCb(idleCallback);
addQueueEmptyForElCb(idleCallback);

function clearRootNode(host, rootNode, onStateChange) {
	sandbox(onStateChange, {
		args: [rootNode, () => null, {}, host[HELPERS_DEFER]],
		host,
		details: {
			type: 'onStateChange'
		},
		location: locations.RENDERER,
		dispatch: host[DISPATCH],
		log
	});
}

function getResolvedState(hostVariables, componentRegistry) {
	return {
		...hostVariables[STATE],
		properties: resolveSelectedProperties(
			componentRegistry,
			selectablePropertyResolver,
			{...hostVariables[PROPS]},
			hostVariables[PROPERTY_DEFS],
			hostVariables[PROPERTIES_WITH_SELECTOR]
		)
	};
}

export default function createElementClass(config) {
	const {
		componentRegistry,
		presentational,
		state: initialState,
		renderer: {
			type: {
				onStateChange,
				onConnect: rendererOnConnect,
				onDisconnect: rendererOnDisconnect,
				deprioritizeStyles = false // eslint-disable-line no-unused-vars
			},
			transformState = props => props,
			coeffects: useCoeffectsLikeObjectInView,
			view
		},
		name: tagName
	} = config;

	let initialized = false;
	let normalizedConfig;
	let onConnect;
	let onDisconnect;
	let combinedPropertyDefs;
	let shadowRootOptions;
	let validateProperties;
	let propertyKeys;
	const selectabledProperties = [];
	componentRegistryForInteractive = componentRegistryForInteractive || componentRegistry;

	function initialize() {
		if (initialized) return;

		initialized = true;
		normalizedConfig = normalizeConfig(config);
		const lifecycleFns = createLifecycleFunctions(
			rendererOnConnect,
			rendererOnDisconnect,
			deprioritizeStyles
		);
		onConnect = lifecycleFns.onConnect;
		onDisconnect = lifecycleFns.onDisconnect;

		combinedPropertyDefs = normalizedConfig.properties;
		shadowRootOptions = normalizedConfig.shadowRootOptions;

		validateProperties = buildPropertiesValidator(combinedPropertyDefs);
		propertyKeys = Object.keys(combinedPropertyDefs);

		for (const k in combinedPropertyDefs) {
			if (combinedPropertyDefs[k].selectable) selectabledProperties.push(k);
		}

		const {length: propertyKeysCount} = propertyKeys;
		for (let i = 0; i < propertyKeysCount; i++) {
			const name = propertyKeys[i];
			accessors[name] = {
				get() {
					return this.getProperty(name);
				},
				set(value) {
					this[SET_PROPERTY](name, value);
				}
			};
		}

		Object.defineProperties(SeismicElement.prototype, accessors);

		assertValidPropertyNames(propertyKeys);
	}

	class SeismicElement extends HTMLElement {
		constructor() {
			super();

			initialize();

			const variables = {};
			this[VARIABLES] = variables;

			variables[SELECTABLE_PROPERTIES] = selectabledProperties;
			variables[PROPERTIES_WITH_SELECTOR] = {};
			variables[PRESENTATIONAL] = presentational;
			variables[PROPS] = {};
			variables[CHILDREN_TO_BE_INTERACTIVE] = {};
			variables[INTERSECTED] = true;
			variables[CONFIG] = normalizedConfig;
			variables[PROPERTY_DEFS] = combinedPropertyDefs;
			if (!presentational) {
				variables[TARGETED_ACTION_TYPES] = [];
				variables[PREVIOUS_RENDER_STATE] = {};
				variables[PREVIOUS_RENDER_PROPERTIES] = {};
				variables[PREVIOUS_TREE_RENDER_STATE] = {};
				variables[PREVIOUS_TREE_RENDER_PROPERTIES] = {};
				variables[RENDERED_ONCE] = false;
				variables[TREE_RENDERED_ONCE] = false;
				variables[ACTION_HANDLERS] = [];
				variables[RUNTIME_ACTION_HANDLERS] = [];
			}

			variables[VERSION] = version; // For CSS externalization. TODO Revisit this logic

			const shadowRoot = this.attachShadow(shadowRootOptions);
			variables[ROOT_NODE] = shadowRoot;
			variables[ATTACHED_ROOT_NODE] = shadowRoot;
			this.upgradeProperties();

			// ui-metrics checks this to know if marks for this component are valid.
			// Components that are not bootstrapped don't have enough data to emit
			// a valid mark
			this.__DO_NOT_USE_BOOTSTRAPPED__ = false;

			variables[STYLE_PROXY] = new Proxy(super.style, {
				set: (style, key, value) => {
					style[key] = value; // eslint-disable-line no-param-reassign
					if (this.__DO_NOT_USE_BOOTSTRAPPED__ && isHoisted(this))
						getHoistTarget(variables).style[key] = value;

					return true;
				},
				get(style, key) {
					if (typeof style[key] === 'function') return style[key].bind(style);

					return style[key];
				}
			});

			setStat('constructed', this.tagName.toLowerCase());
		}

		get [CONFIG]() {
			return normalizedConfig;
		}

		get [PROPERTY_DEFS]() {
			return combinedPropertyDefs;
		}

		get version() {
			return version;
		}

		static get observedAttributes() {
			const pKeys = Object.keys(
				mergeBehaviorProperties(
					normalizeBehaviors(config.behaviors || [], tagName),
					config.properties || {},
					tagName
				)
			);
			return pKeys.map(toAttributeName);
		}

		upgradeProperties() {
			const {length: propertyKeysCount} = propertyKeys;
			for (let i = 0; i < propertyKeysCount; i++) {
				const name = propertyKeys[i];
				if (this.hasOwnProperty(name)) {
					const value = this[name];
					delete this[name];
					this[name] = value;
				}
			}
		}

		getComponentId() {
			const variables = getHostVariables(this);
			return variables[COMPONENT_ID];
		}

		setComponentId() {
			const variables = getHostVariables(this);
			variables[COMPONENT_ID] = this.getAttribute(COMPONENT_ID_ATTRIBUTE) || guid();
			this.setAttribute(NOW_ID_ATTR, variables[COMPONENT_ID], false);
			this.setAttribute(COMPONENT_ID_ATTRIBUTE, variables[COMPONENT_ID], false);
		}

		getProperty(name) {
			const variables = getHostVariables(this);
			return variables[PROPS][name];
		}

		[SET_PROPERTY](name, value, updateProperties = false, interactionId = createInteractionId()) {
			const variables = getHostVariables(this);

			if (variables[PROPS][NOW_MODE_PROP] === COMPONENT_MODE_DETACH) {
				if (name !== NOW_MODE_PROP) {
					if (!this[DETACHED_COMPONENT_PROPERTY_MAP])
						this[DETACHED_COMPONENT_PROPERTY_MAP] = new Map();

					this[DETACHED_COMPONENT_PROPERTY_MAP].set(name, {
						name,
						value,
						updateProperties,
						interactionId
					});
					return;
				}
			}

			const valueOrDefault = parseProperty(
				name,
				value === undefined ? combinedPropertyDefs[name].default : value,
				combinedPropertyDefs[name],
				this,
				variables[DISPATCH] || createDispatcher(this)
			);

			if (quickEquals(valueOrDefault, variables[PROPS][name])) {
				return;
			}

			// we might want to consider doing some of this in createUpdateProperties like we
			// do for createUpdateState, so that we never even get this far if the property
			// update is declarative
			if (
				(enableDeepPropertyCompare && !optOutDeepCompare) || combinedPropertyDefs[name].deepCompare
					? isEqual(variables[PROPS][name], valueOrDefault)
					: variables[PROPS][name] === valueOrDefault
			)
				return;

			if (this.__DO_NOT_USE_BOOTSTRAPPED__ !== true) {
				variables[PROPS][name] = valueOrDefault;
				return;
			}

			if (!shouldSetProperty(tagName, name, combinedPropertyDefs, updateProperties)) {
				return log(`Cannot set read only or computed property ${name}.`, {
					host: this,
					origin: String(SET_PROPERTY)
				});
			}

			if (variables[PROPS][NOW_ENABLE_PROPERTY_SETTING_PREVENTION_PROP]) {
				const event = variables[DISPATCH](
					COMPONENT_PROPERTY_UPDATE_REQUESTED,
					{name, value: variables[PROPS][name], nextValue: valueOrDefault, host: this},
					setInteractionId(
						{
							[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true,
							[COMPONENT_INTERNAL_ACTION]: true,
							internal: true,
							internalPropertyUpdateRequested: updateProperties
						},
						interactionId
					)
				);

				if (event.defaultPrevented) return;
			}

			const previousValue = variables[PROPS][name];
			variables[PROPS][name] = valueOrDefault;

			// If nowPlaceholderHeight or Width changes, we want to replace the previous intersect styles so long as:
			// We are currently observing for an intersection (because otherwise we don't want to apply these styles)
			// We have not intersected yet (these styles should only be applied when the component has not intersected yet)
			if (
				variables[USING_INTERSECTION_OBSERVER] &&
				[NOW_PLACEHOLDER_HEIGHT_PROP, NOW_PLACEHOLDER_WIDTH_PROP].includes(name) &&
				!variables[INTERSECTED] &&
				previousValue !== valueOrDefault
			) {
				const removeIntersectedStyles = applyIntersectStyles(this, config, {
					height: variables[PROPS][NOW_PLACEHOLDER_HEIGHT_PROP],
					width: variables[PROPS][NOW_PLACEHOLDER_WIDTH_PROP]
				});

				if (variables[REMOVE_INTERSECTED_STYLES]) variables[REMOVE_INTERSECTED_STYLES]();

				variables[REMOVE_INTERSECTED_STYLES] = removeIntersectedStyles;
			}

			const propertyDetails = {
				previousValue,
				value: valueOrDefault
			};

			updatePropertiesWithSelector(name, valueOrDefault, variables[PROPERTIES_WITH_SELECTOR]);
			updateSelectedPropertyMappings(valueOrDefault, name, variables[COMPONENT_ID]);
			updateResolvedSelectedPropertyMappings(
				propertyDetails,
				name,
				variables[COMPONENT_ID],
				componentRegistry,
				normalizedConfig.properties[name],
				variables[PROPERTIES_WITH_SELECTOR]
			);
			this[UPDATE_AFFECTED_COMPONENTS]({name, propertyDetails, interactionId, includeSource: true});
		}

		[UPDATE_AFFECTED_COMPONENTS]({name, propertyDetails, interactionId, includeSource}) {
			const variables = getHostVariables(this);

			const allAffectedComponents = getAffected(
				name,
				variables[COMPONENT_ID],
				componentRegistry,
				includeSource
			);
			triggerComponentUpdates(
				componentRegistry,
				allAffectedComponents,
				propertyDetails,
				interactionId,
				'PROPERTY_SET'
			);
		}

		[HOIST_CHANGED](value) {
			const variables = getHostVariables(this);

			if ((value && variables[UNHOIST]) || (!value && !variables[UNHOIST])) return;

			if (!presentational) {
				variables[ACTION_HANDLERS] = removeActionHandlers(variables[ACTION_HANDLERS]);
				variables[TARGETED_ACTION_TYPES] = [];
			}

			if (value) {
				// render null to host's shadowRoot to ensure cleanup
				clearRootNode(this, variables[ATTACHED_ROOT_NODE], onStateChange);
				variables[UNHOIST] = hoist(this, deprioritizeStyles, onStateChange);
			} else {
				typeof variables[UNHOIST] === 'function' && variables[UNHOIST]();
				delete variables[UNHOIST];
			}

			if (!presentational)
				variables[ACTION_HANDLERS] = addActionHandlers(this, normalizedConfig.interceptors);
		}

		[HOIST_PARENT_CHANGED](propName) {
			const variables = getHostVariables(this);

			const resolvedState = getResolvedState(variables, componentRegistry, this);
			// reflect hoist targeting properties immediately, these attributes are used when hoisting
			[HOIST_ATTR, NOW_HOIST_PROP, propName].forEach(p => {
				reflectProperty(p, combinedPropertyDefs[p], resolvedState, this, {});
			});
			if (!presentational) {
				variables[ACTION_HANDLERS] = removeActionHandlers(variables[ACTION_HANDLERS]);
				variables[TARGETED_ACTION_TYPES] = [];
			}

			typeof variables[UNHOIST] === 'function' && variables[UNHOIST]();
			variables[UNHOIST] = hoist(this, deprioritizeStyles, onStateChange);

			if (!presentational)
				variables[ACTION_HANDLERS] = addActionHandlers(this, normalizedConfig.interceptors);
		}

		[GET_RESOLVED_PROPERTY](propName) {
			const variables = getHostVariables(this);

			return resolveSelectedProperty(
				componentRegistry,
				selectablePropertyResolver,
				variables[PROPS][propName],
				normalizedConfig.properties[propName]
			);
		}

		setAttribute(name, value, isReflecting = false) {
			if (!shouldSetAttribute(tagName, isReflecting, name, combinedPropertyDefs)) {
				return log(`Cannot set read only or computed property ${name}`, {
					host: this,
					origin: 'setAttribute'
				});
			}

			if (shouldHoistAttribute(this, name)) hoistAttribute(this, name, value);

			return HTMLElement.prototype.setAttribute.call(this, name, value);
		}

		removeAttribute(name) {
			if (shouldHoistAttribute(this, name)) unhoistAttribute(this, name);

			HTMLElement.prototype.removeAttribute.call(this, name);
		}

		generateDeferGetterAndSetter() {
			const variables = getHostVariables(this);

			variables[DEFERRED] = true;
			Object.defineProperty(this, DEFER_ATTRIBUTE, {
				get: () => variables[DEFERRED],
				set: value => {
					if (variables[DEFERRED] === value) return;

					variables[DEFERRED] = value;
					if (!variables[CONNECTED]) {
						setStat('undefer', this.tagName.toLowerCase());
						this.connectedCallback();
					} else {
						log(
							`${this.tagName.toLowerCase()}[component-id="${
								variables[COMPONENT_ID]
							}"] cannot be deferred. It is already connected.`,
							{host: this, origin: 'generateDeferGetterAndSetter'}
						);
					}
				},
				configurable: true
			});
		}

		shouldLegacyDefer() {
			return this.hasAttribute(DEFER_ATTRIBUTE) && !this.__DO_NOT_USE_BOOTSTRAPPED__;
		}

		setInitialProperties() {
			const variables = getHostVariables(this);

			const defaults = {};
			const {length: propertyKeysCount} = propertyKeys;
			for (let i = 0; i < propertyKeysCount; i++) {
				const name = propertyKeys[i];
				defaults[name] = getInitialPropertyValue(this, name, combinedPropertyDefs);
			}

			const propertyDefs = combinedPropertyDefs;
			variables[PROPS] = parseProperties(
				resolveProperties(
					initialState,
					filterPropertyDefinitions(propertyDefs, def => !def.computed),
					defaults
				),
				propertyDefs,
				{host: this, dispatch: variables[DISPATCH]}
			);

			const properties = variables[PROPS];
			for (const k in properties) {
				updatePropertiesWithSelector(k, properties[k], variables[PROPERTIES_WITH_SELECTOR]);
				updateSelectedPropertyMappings(
					properties[k],
					k,
					variables[COMPONENT_ID],
					this.__DO_NOT_USE_BOOTSTRAPPED__
				);

				if (combinedPropertyDefs[k] && combinedPropertyDefs[k].selectable) {
					addResolvedSelectedPropertyMappingWithResolvedValue(
						properties[k],
						k,
						variables[COMPONENT_ID]
					);
					triggerComponentUpdates(
						componentRegistry,
						getComponentAffected(k, variables[COMPONENT_ID]),
						{
							value: variables[PROPS][k]
						},
						undefined,
						'INITIAL_PROPERTY_SET'
					);
				}
			}
		}

		getResolvedProperties(interactionId) {
			const variables = getHostVariables(this);
			const state = getResolvedState(variables, componentRegistry, this);
			const resolvedProperties = resolveComputedProperties(state, this, variables, interactionId);
			return unescapeReservedCharacters(resolvedProperties).properties;
		}

		getProperties() {
			const variables = getHostVariables(this);

			return {...variables[PROPS]};
		}

		attributeChangedCallback(name, oldValue, newValue) {
			const variables = getHostVariables(this);
			if (name === DEFER_ATTRIBUTE) return (this[DEFER_ATTRIBUTE] = newValue);

			if (!variables[CONNECTED]) return;

			const propName = toPropertyName(name);
			const propValue = this[propName];
			if (isReflecting(name)) return clearReflection(name);

			// vdom renderers set prop, then attribute values on nodes; this check prevents a double render
			// while allowing for direct attribute setting and removal, e.g., el.setAttribute, el.removeAttribute
			// 1. if newValue is null and propValue is falsey then the property was set already and removeAttribute was called
			// 2. if propValue is truthy and newValue is a string and values are equal then the property was set
			//    already and setAttribute was called
			// 3. if propValue is truthy and newValue is empty string setAttribute was called after setting property to true
			// 4. if propValue is falsey and newValue is null removeAttribute was called after setting property to false
			if (newValue === null && !propValue) return;

			if (propValue && typeof newValue === 'string' && propValue === newValue) return;

			if (typeof propValue === 'boolean') {
				if (
					propValue &&
					newValue === '' &&
					!combinedPropertyDefs[propName].unstablePreserveEmptyStr
				)
					return;

				if (!propValue && newValue === null) return;
			}

			// if newValue is null then removeAttribute was called, We should set the property to undefined, which will
			// be set to default in the setter if necessary
			if (variables[CONNECTED] && oldValue !== newValue)
				this[propName] = newValue === null ? undefined : newValue;
		}

		connectedCallback() {
			const variables = getHostVariables(this);
			// if a node is quickly connected and disconnected then by the time
			// connectedCallback is called it can actually be disconnected
			if (!this.isConnected) return;
			if (this.shouldLegacyDefer()) {
				variables[CONNECTED_DEFERRED_TIMESTAMP] = performance.now();
				setStat('defer', this.tagName.toLowerCase());
				return this.generateDeferGetterAndSetter();
			}

			// We haven't called `setInitialProperties()` yet until later in the
			// connect, so `this[NOW_PRIORITY_PROP]` may not have the default value
			// from the property config.
			const priorityLevel =
				this[NOW_PRIORITY_PROP] === undefined
					? combinedPropertyDefs[NOW_PRIORITY_PROP]
						? combinedPropertyDefs[NOW_PRIORITY_PROP].default
						: undefined
					: this[NOW_PRIORITY_PROP];
			if (priorityLevel === PRIORITY_LEVEL_LOW && !variables[LOW_PRIORITY_PENDING]) {
				this.setAttribute(NOW_PRIORITY_ATTR, priorityLevel);
				variables[LOW_PRIORITY_PENDING] = true;
				schedule({
					fn: () => {
						if (!variables[CONNECTED]) this.connectedCallback();
						this.removeAttribute(NOW_PRIORITY_ATTR);
						delete variables[LOW_PRIORITY_PENDING];
					},
					type: DEFER_IDLE,
					meta: {el: this, interactionId: createInteractionId()}
				});
				return;
			}

			variables[CONNECTED] = true;
			variables[CONNECTED_TIMESTAMP] = performance.now();
			this.setComponentId();
			this.dispatch = createDispatcher(this);
			variables[HELPERS_SCHEDULED] = createHelpers(
				this,
				combinedPropertyDefs,
				HELPERS_SCHEDULED_CONST,
				{presentational}
			);
			variables[HELPERS_IMMEDIATE] = createHelpers(
				this,
				combinedPropertyDefs,
				HELPERS_IMMEDIATE_CONST,
				{presentational}
			);
			variables[HELPERS_DEFER] = createHelpers(this, combinedPropertyDefs, HELPERS_DEFER_CONST, {
				presentational
			});
			this.helpers = variables[HELPERS_SCHEDULED]; // @servicenow/ui-internal relies on this
			variables[DISPATCH] = this.dispatch;
			variables[CONDITIONAL_DISPATCH] = createConditionalDispatch(this);
			variables[UNHOIST] = hoist(this, deprioritizeStyles, onStateChange);
			const rootNode = getRootNode(this);
			variables[ANCESTOR_ROOT_NODE] = rootNode && rootNode.nowId ? rootNode : null;
			if (variables[ANCESTOR_ROOT_NODE]) {
				variables[ANCESTOR_ROOT_NODE][VARIABLES][CHILDREN_TO_BE_INTERACTIVE][
					variables[COMPONENT_ID]
				] = true;
			}
			this.__DO_NOT_USE_OR_YOU_WILL_BE_FIRED_BY_YOUR_PARENT__ =
				(variables[ANCESTOR_ROOT_NODE] && variables[ANCESTOR_ROOT_NODE].nowId) || null;
			variables[CHILDREN_TO_BE_RENDERED] = {};
			variables[NEEDS_TO_KNOW_ABOUT_TREE_RENDERS] =
				normalizedConfig.shouldDispatch[COMPONENT_DOM_TREE_READY] ||
				normalizedConfig.shouldDispatch[COMPONENT_TREE_RENDERED] ||
				(variables[ANCESTOR_ROOT_NODE] &&
					variables[ANCESTOR_ROOT_NODE][VARIABLES][NEEDS_TO_KNOW_ABOUT_TREE_RENDERS]);

			if (!presentational)
				variables[ACTION_HANDLERS] = addActionHandlers(this, normalizedConfig.interceptors);

			if (componentRegistry[this.getComponentId()] !== undefined)
				log(
					`The componentId- ${this.getComponentId()} already exists. Component identifiers must be unique.`
				);

			componentRegistry[this.getComponentId()] = this;

			// This must be called here syncronously instead of in the callback of onConnect
			// just below. If any code gets added that could prevent this[RENDER] being called
			// between here and inside the onConnect callback just below, it will break the
			// TREE_RENDERED and TREE_READY lifecycles
			this[NOTIFY_PARENT_THAT_CHILD_IS_RENDERING]();

			sandbox(onConnect, {
				args: [
					variables[ROOT_NODE],
					this.dispatch,
					() => {
						this.setInitialProperties();
						if (!presentational && !variables[STATE]) {
							// Set variables[STATE] to just initialState and not include setInitialState
							// for backwards compatibility: so that initialState is still available
							// within a computed property's computed() method
							variables[STATE] = {...normalizedConfig.initialState};

							const initialState = {
								...variables[STATE],
								...normalizedConfig.setInitialState({
									host: this,
									properties: unescapeReservedCharacters(
										computeProperties(
											camelCaseProperties(getResolvedState(variables, componentRegistry, this)),
											this
										)
									).properties,
									context: createContextProxy(this)
								})
							};

							emitNowUiEvent(STATE_UPDATED, {
								componentId: variables[COMPONENT_ID],
								previousState: undefined,
								nextState: initialState
							});
							variables[STATE] = initialState;
						}

						this[RENDER]();

						// this is to handle the case in which a renderer has not already set the properties on the host because it is async.
						// i do not want to unconditionally move hoisting initialization here without a design and supporting tests.
						// this change is backwards compatible with existing hoist use cases and renderers.
						if (typeof variables[UNHOIST] !== 'function' && isHoisted(this)) {
							if (presentational) {
								variables[UNHOIST] = hoist(this, deprioritizeStyles, onStateChange);
							} else {
								removeActionHandlers(variables[ACTION_HANDLERS]);
								variables[TARGETED_ACTION_TYPES] = [];
								variables[UNHOIST] = hoist(this, deprioritizeStyles, onStateChange);
								variables[ACTION_HANDLERS] = addActionHandlers(this, normalizedConfig.interceptors);
							}
						}

						applyStyles(this, config);

						variables[USING_INTERSECTION_OBSERVER] =
							variables[PROPS][NOW_PLACEHOLDER_HEIGHT_PROP] ||
							variables[PROPS][NOW_PLACEHOLDER_WIDTH_PROP];
						if (variables[USING_INTERSECTION_OBSERVER]) {
							variables[INTERSECTED] = false;
							if (
								variables[PROPS][NOW_PLACEHOLDER_HEIGHT_PROP] ||
								variables[PROPS][NOW_PLACEHOLDER_WIDTH_PROP]
							) {
								variables[REMOVE_INTERSECTED_STYLES] = applyIntersectStyles(this, config, {
									height: variables[PROPS][NOW_PLACEHOLDER_HEIGHT_PROP],
									width: variables[PROPS][NOW_PLACEHOLDER_WIDTH_PROP]
								});
								intersectionObserver.observe(this);
							}
						}

						if (!this.__DO_NOT_USE_BOOTSTRAPPED__) {
							this.__DO_NOT_USE_BOOTSTRAPPED__ = true;

							variables[CONDITIONAL_DISPATCH](
								COMPONENT_BOOTSTRAPPED,
								{host: this},
								{internal: true}
							);
						}

						variables[CONDITIONAL_DISPATCH](COMPONENT_CONNECTED, {host: this}, {internal: true});
					}
				],
				host: this,
				details: {
					type: 'onConnect'
				},
				location: locations.RENDERER,
				dispatch: variables[DISPATCH],
				log
			});
		}

		disconnectedCallback() {
			const variables = getHostVariables(this);

			if (!variables[CONNECTED]) return;

			variables[CONNECTED] = false;
			// render null to ensure that view specific clean up hooks are called
			clearRootNode(this, variables[ROOT_NODE], onStateChange);

			removeStyles(this);
			if (variables[USING_INTERSECTION_OBSERVER] && !variables[INTERSECTED])
				intersectionObserver.unobserve(this);

			if (
				(!normalizedConfig.shouldDispatch[COMPONENT_BOOTSTRAPPED] &&
					!normalizedConfig.shouldDispatch[COMPONENT_CONNECTED]) ||
				(normalizedConfig.shouldDispatch[COMPONENT_BOOTSTRAPPED] &&
					variables[BOOTSTRAPPED_HANDLED]) ||
				(normalizedConfig.shouldDispatch[COMPONENT_CONNECTED] && variables[CONNECTED_HANDLED])
			) {
				variables[CONDITIONAL_DISPATCH](
					COMPONENT_DISCONNECTED,
					{host: this},
					{
						internal: true,
						[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true
					}
				);
			}

			sandbox(onDisconnect, {
				args: [
					variables[ROOT_NODE],
					this.dispatch,
					() => {
						typeof variables[UNHOIST] === 'function' && variables[UNHOIST]();
					}
				],
				host: this,
				details: {
					type: 'onDisconnect'
				},
				location: locations.RENDERER,
				dispatch: variables[DISPATCH],
				log
			});

			if (presentational) {
				removeMappings(variables[COMPONENT_ID]);
			} else {
				variables[CONNECTED_HANDLED] = false;
				variables[ACTION_HANDLERS] = removeActionHandlers(variables[ACTION_HANDLERS]);
				variables[TARGETED_ACTION_TYPES] = [];
				removeMappings(variables[COMPONENT_ID]);
				variables[RENDERED_ONCE] = false;
				variables[TREE_RENDERED_ONCE] = false;
				clearPendingNetworkActionsForHost(this);
			}

			removeContextConsumer(this);

			// there are cases where connectedCallback has not executed, but disconnectedCallback does
			this.getComponentId() && delete componentRegistry[this.getComponentId()];
		}

		[RENDER](interactionId = createInteractionId(), source = metrics.CONNECTION, options = {}) {
			const variables = getHostVariables(this);

			options.cause = source; // eslint-disable-line no-param-reassign

			if (enableSchedulerQueueCounts) {
				options.highPriorityTaskCount = countHighPriorityTasks(this);
				options.mediumPriorityTaskCount = countMediumPriorityTasks(this);
				options.pendingNetworkActionCount = pendingNetworkActionCountForHost(this);
			}

			// tell parent that a child is rendering. Once this is called,
			// this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]() must be called at some point,
			// either directly or via this[CHECK_IF_TREE_RENDERED]()
			this[NOTIFY_PARENT_THAT_CHILD_IS_RENDERING]({interactionId});

			if (!this.isConnected) {
				this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});

				return;
			}

			schedule({
				fn: () => {
					if (!this.isConnected) {
						this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});

						return;
					}

					if (variables[PROPS][NOW_MODE_PROP] === COMPONENT_MODE_SUSPEND) {
						this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});
						return;
					}

					if (variables[PROPS][NOW_MODE_PROP] === COMPONENT_MODE_DETACH) {
						const fragment = document.createDocumentFragment();

						if (!this[DETACHED_SHADOW_ROOT_CHILDREN_FRAGMENT]) {
							while (variables[ROOT_NODE].firstChild) {
								fragment.appendChild(
									variables[ROOT_NODE].removeChild(variables[ROOT_NODE].firstChild)
								);
							}
							this[DETACHED_SHADOW_ROOT_CHILDREN_FRAGMENT] = fragment;
							this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});
						}
						return;
					}

					// If we are observing for an intersection, and it has not happened yet, then cancel the render
					if (variables[USING_INTERSECTION_OBSERVER] && !variables[INTERSECTED]) {
						this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});

						return;
					}

					const state = getResolvedState(variables, componentRegistry);
					validateProperties(state.properties, this);
					validateContext(variables[CONTEXT], this);

					const updatedState = shapeRenderData(
						state,
						this,
						variables,
						transformState,
						variables[DISPATCH],
						interactionId
					);

					const event = variables[CONDITIONAL_DISPATCH](
						COMPONENT_RENDER_REQUESTED,
						{
							previousRenderState: variables[PREVIOUS_RENDER_STATE],
							previousRenderProperties: variables[PREVIOUS_RENDER_PROPERTIES],
							host: this
						},
						{
							firstRenderOnConnect: !variables[RENDERED_ONCE],
							[COMPONENT_INTERNAL_ACTION]: true,
							internal: true,
							[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true,
							renderCause: options.cause
						}
					);

					if (event && event.defaultPrevented) {
						this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});
						return;
					}

					let shouldSkipRender = false;
					if (this[DETACHED_SHADOW_ROOT_CHILDREN_FRAGMENT]) {
						if (this[DETACHED_COMPONENT_PROPERTY_MAP]) {
							this[DETACHED_COMPONENT_PROPERTY_MAP].forEach(
								({name, value, updateProperties, interactionId}) => {
									shouldSkipRender = shouldSkipRender || this[VARIABLES][PROPS][name] !== value;
									this[SET_PROPERTY](name, value, updateProperties, interactionId);
								}
							);
						}
						variables[ROOT_NODE].appendChild(this[DETACHED_SHADOW_ROOT_CHILDREN_FRAGMENT]);
						delete this[DETACHED_SHADOW_ROOT_CHILDREN_FRAGMENT];
						delete this[DETACHED_COMPONENT_PROPERTY_MAP];
						if (shouldSkipRender) return;
					}
					measure(
						() => {
							const [state, helpers] = createOnStateChangeArgs(
								useCoeffectsLikeObjectInView,
								variables[COMPONENT_ID],
								updatedState,
								createContextProxy(this),
								applyMetrics(variables[HELPERS_DEFER]),
								this
							);
							sandbox(onStateChange, {
								args: [variables[ROOT_NODE], view, state, helpers, interactionId],
								host: this,
								details: {
									type: 'onStateChange'
								},
								location: locations.RENDERER,
								dispatch: variables[DISPATCH],
								log
							});
						},
						this,
						`render-${tagName}#${variables[COMPONENT_ID]}`,
						interactionId,
						options
					);

					if (!variables[RENDERED_ONCE]) {
						variables[CONDITIONAL_DISPATCH](
							COMPONENT_DOM_READY,
							{
								previousRenderState: variables[PREVIOUS_RENDER_STATE],
								previousRenderProperties: variables[PREVIOUS_RENDER_PROPERTIES],
								host: this
							},
							{
								internal: true,
								firstRenderOnConnect: true,
								[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true
							}
						);
					}
					variables[CONDITIONAL_DISPATCH](
						COMPONENT_RENDERED_ACTION_TYPE,
						{
							previousRenderState: variables[PREVIOUS_RENDER_STATE],
							previousRenderProperties: variables[PREVIOUS_RENDER_PROPERTIES],
							host: this
						},
						{
							internal: true,
							firstRenderOnConnect: !variables[RENDERED_ONCE],
							[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true
						}
					);

					variables[RENDERED_ONCE] = true;
					variables[RENDERED_ONCE_TIMESTAMP] =
						variables[RENDERED_ONCE_TIMESTAMP] || performance.now();
					variables[PREVIOUS_RENDER_STATE] = updatedState;
					variables[PREVIOUS_RENDER_PROPERTIES] = state.properties;

					this[CHECK_IF_TREE_RENDERED](interactionId, options);
				},
				type: COMPONENT_RENDER,
				meta: {el: this, interactionId}
			});
		}

		[INTERSECTION_TRIGGERED]() {
			const variables = getHostVariables(this);

			variables[INTERSECTED] = true;
			if (variables[REMOVE_INTERSECTED_STYLES]) {
				variables[REMOVE_INTERSECTED_STYLES]();
				variables[REMOVE_INTERSECTED_STYLES] = null;
			}

			this[RENDER](createInteractionId(), metrics.INTERSECTION);
		}

		[CHECK_IF_TREE_RENDERED](interactionId, options = {}) {
			const variables = getHostVariables(this);

			if (enableSchedulerQueueCounts) {
				options.highPriorityTaskCount = countHighPriorityTasks(this);
				options.mediumPriorityTaskCount = countMediumPriorityTasks(this);
				options.pendingNetworkActionCount = pendingNetworkActionCountForHost(this);
			}

			// Check if all children are complete. if so, dispatch that this component is also complete
			if (Object.keys(variables[CHILDREN_TO_BE_RENDERED]).length) return;

			if (!variables[TREE_RENDERED_ONCE]) {
				variables[CONDITIONAL_DISPATCH](
					COMPONENT_DOM_TREE_READY,
					{
						previousRenderState: variables[PREVIOUS_TREE_RENDER_STATE],
						previousRenderProperties: variables[PREVIOUS_TREE_RENDER_PROPERTIES],
						host: this
					},
					{
						internal: true,
						firstRenderOnConnect: true,
						[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true
					}
				);
			}

			mark(this, interactionId, metrics.RENDER_TREE, options);
			variables[CONDITIONAL_DISPATCH](
				COMPONENT_TREE_RENDERED,
				{
					previousRenderState: variables[PREVIOUS_TREE_RENDER_STATE],
					previousRenderProperties: variables[PREVIOUS_TREE_RENDER_PROPERTIES],
					host: this
				},
				{
					internal: true,
					firstRenderOnConnect: !variables[TREE_RENDERED_ONCE],
					[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true
				}
			);

			variables[TREE_RENDERED_ONCE] = true;
			variables[TREE_RENDERED_ONCE_TIMESTAMP] =
				variables[TREE_RENDERED_ONCE_TIMESTAMP] || performance.now();
			variables[PREVIOUS_TREE_RENDER_STATE] = variables[PREVIOUS_RENDER_STATE];
			variables[PREVIOUS_TREE_RENDER_PROPERTIES] = variables[PREVIOUS_RENDER_PROPERTIES];

			this[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId});
		}

		[CHILD_IS_RENDERING](child) {
			const variables = getHostVariables(this);
			const childVariables = getHostVariables(child);
			variables[CHILDREN_TO_BE_RENDERED][childVariables[COMPONENT_ID]] = true;
		}

		[CHILD_HAS_RENDERED]({child, interactionId}) {
			const variables = getHostVariables(this);
			const childVariables = getHostVariables(child);
			delete variables[CHILDREN_TO_BE_RENDERED][childVariables[COMPONENT_ID]];

			this[CHECK_IF_TREE_RENDERED](interactionId);
		}

		[NOTIFY_PARENT_THAT_CHILD_IS_RENDERING]() {
			const variables = getHostVariables(this);
			const parentHost = variables[ANCESTOR_ROOT_NODE];
			if (parentHost && getHostVariables(parentHost)[NEEDS_TO_KNOW_ABOUT_TREE_RENDERS])
				parentHost[CHILD_IS_RENDERING](this);
		}

		[NOTIFY_PARENT_THAT_CHILD_TREE_HAS_RENDERED]({interactionId}) {
			const variables = getHostVariables(this);
			const parentHost = variables[ANCESTOR_ROOT_NODE];
			if (parentHost && getHostVariables(parentHost)[NEEDS_TO_KNOW_ABOUT_TREE_RENDERS])
				parentHost[CHILD_HAS_RENDERED]({child: this, interactionId});
		}
	}

	const accessors = {
		nowElementConfig: {
			get() {
				initialize();
				return normalizedConfig;
			}
		},
		nowRootNode: {
			get() {
				if (!initialized || shadowRootOptions.mode === SHADOW_ROOT_MODE_CLOSED) return null;

				return getHostVariables(this)[ROOT_NODE];
			}
		},
		style: {
			get() {
				return getHostVariables(this)[STYLE_PROXY];
			},
			set() {
				log('setting style property directly is not supported');
			}
		}
	};
	return SeismicElement;
}
